'use client';

import type { ReactNode } from 'react';
import type { FeatureFlagToggle } from '@/libs/feature-flags/types';
// Since QueryClientProvider relies on useContext under the hood, we have to put 'use client' on top
import { QueryClientProvider } from '@tanstack/react-query';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';

import IntercomWidget from '@/components/entities/analytics/components/IntercomWidget/IntercomWidget';
import { PostHogIdentitySync } from '@/components/entities/analytics/components/PostHog/PostHogIdentitySync';
import { GmDrawerRouteReset } from '@/components/features/gamification/bars/components/GmDrawerRouteReset';
import AppStartupEffect from '@/components/features/layout/AppStartupEffect';
import MarketRouteSync from '@/components/features/layout/MarketRouteSync';
import { WebSocketProvider } from '@/contexts/websockets/websocket-provider';
import { FeatureFlagsIdentitySync, FeatureFlagsProvider } from '@/libs/feature-flags/client';
import { getQueryClient } from '@/queries/queryClient';
import { ReduxProvider } from '@/redux/redux-provider';

type ProvidersProps = {
  children: ReactNode;
  featureFlagsBootstrap?: FeatureFlagToggle[];
};

export default function Providers({ children, featureFlagsBootstrap }: ProvidersProps) {
  // NOTE: Avoid useState when initializing the query client if you don't
  //       have a suspense boundary between this and the code that may
  //       suspend because React will throw away the client on the initial
  //       render if it suspends and there is no boundary
  const queryClient = getQueryClient();

  return (
    <FeatureFlagsProvider bootstrap={featureFlagsBootstrap}>
      <PostHogProvider client={posthog}>
        <ReduxProvider>
          <QueryClientProvider client={queryClient}>
            <FeatureFlagsIdentitySync />
            <PostHogIdentitySync />
            <WebSocketProvider>
              <AppStartupEffect />
              <MarketRouteSync />
              <GmDrawerRouteReset />
              <IntercomWidget>
                {children}
              </IntercomWidget>
            </WebSocketProvider>
          </QueryClientProvider>
        </ReduxProvider>
      </PostHogProvider>
    </FeatureFlagsProvider>
  );
}
